import React, { useState, useEffect } from 'react';
import { 
  DocumentArrowUpIcon, 
  DocumentArrowDownIcon,
  FolderIcon,
  CalendarIcon,
  CloudArrowUpIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { STUDENT_TOKEN } from '../../constants';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const StudentFileSharing = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedLessonPack, setSelectedLessonPack] = useState(null);
  const [lessonPacks, setLessonPacks] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch lesson packs and their file shares
  useEffect(() => {
    fetchLessonPacks();
  }, []);

  // Fetch files when a lesson pack is selected
  useEffect(() => {
    if (selectedLessonPack) {
      fetchFiles(selectedLessonPack.lesson_pack_id);
    }
  }, [selectedLessonPack]);

  const fetchLessonPacks = async () => {
    try {
      const token = localStorage.getItem(STUDENT_TOKEN);
      const response = await fetch(`${API_URL}/api/lesson-packs/file-shares/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLessonPacks(data.data);
          if (data.data.length > 0) {
            setSelectedLessonPack(data.data[0]);
          }
        } else {
          setError(data.message || 'Failed to fetch lesson packs');
        }
      } else {
        setError('Failed to fetch lesson packs');
      }
    } catch (error) {
      console.error('Error fetching lesson packs:', error);
      setError('Failed to fetch lesson packs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async (lessonPackId) => {
    try {
      const token = localStorage.getItem(STUDENT_TOKEN);
      const response = await fetch(`${API_URL}/api/lesson-packs/${lessonPackId}/files/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFiles(data.data.files || []);
        } else {
          setError(data.message || 'Failed to fetch files');
        }
      } else {
        setError('Failed to fetch files');
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to fetch files');
    }
  };

  const handleFileUpload = async (file) => {
    if (!selectedLessonPack) {
      setError('Please select a lesson pack first');
      return;
    }

    // Debug: Log file information
    console.log('File details:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Fallback for file type detection
    let fileType = file.type;
    if (!fileType || fileType === '') {
      // Extract extension from filename
      const extension = file.name.split('.').pop().toLowerCase();
      const mimeTypes = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'txt': 'text/plain',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'mp4': 'video/mp4',
        'mp3': 'audio/mpeg',
        'zip': 'application/zip'
      };
      fileType = mimeTypes[extension] || 'application/octet-stream';
      console.log('Detected file type from extension:', fileType);
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      const token = localStorage.getItem(STUDENT_TOKEN);

      // Step 1: Initialize S3 upload
      const initResponse = await fetch(`${API_URL}/api/lesson-packs/${selectedLessonPack.lesson_pack_id}/files/s3-upload/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_name: file.name,
          file_type: fileType,
          file_size: file.size,
          uploaded_by: 'Student'
        })
      });

      const initData = await initResponse.json();
      if (!initData.success) {
        throw new Error(initData.message || 'Failed to initialize upload');
      }

      // Step 2: Upload file to S3 using presigned URL with progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress((e.loaded / e.total) * 100);
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve(xhr);
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });
        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });
        xhr.open('PUT', initData.data.presigned_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Step 3: Confirm upload
      const confirmResponse = await fetch(`${API_URL}/api/lesson-packs/${selectedLessonPack.lesson_pack_id}/files/s3-confirm/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          s3_key: initData.data.s3_key
        })
      });

      const confirmData = await confirmResponse.json();
      if (confirmData.success) {
        setFiles(prev => [confirmData.data, ...prev]);
        setUploadProgress(100);
        setTimeout(() => {
          setUploading(false);
          setUploadProgress(0);
        }, 1000);
      } else {
        throw new Error(confirmData.message || 'Failed to confirm upload');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Upload failed');
      setUploading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const token = localStorage.getItem(STUDENT_TOKEN);
      const response = await fetch(`${API_URL}/api/lesson-packs/${selectedLessonPack.lesson_pack_id}/files/${file.id}/download/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        if (data.data.download_url) {
          // Fetch the file content from S3
          const fileResponse = await fetch(data.data.download_url);
          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            
            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = data.data.file_name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the object URL
            window.URL.revokeObjectURL(url);
          } else {
            setError('Failed to download file from S3');
          }
        } else {
          setError('Download URL not found');
        }
      } else {
        setError(data.message || 'Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      setError('Download failed');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('video')) return '🎥';
    if (fileType.includes('audio')) return '🎵';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    return '📁';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading lesson packs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FolderIcon className="h-8 w-8 text-blue-600" />
          File Sharing
        </h1>
        <p className="text-gray-600">Access and share files for your lesson packages.</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Lesson Packs List */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpenIcon className="h-5 w-5 text-blue-600" />
              Your Lesson Packs
            </h2>
            
            {lessonPacks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No lesson packs found</p>
            ) : (
              <div className="space-y-3">
                {lessonPacks.map((pack) => (
                  <div
                    key={pack.id}
                    onClick={() => setSelectedLessonPack(pack)}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedLessonPack?.id === pack.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {pack.lesson_pack_subject}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          Tutor: {pack.lesson_pack_tutor_name}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          <span>{pack.files_count} files</span>
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {formatDate(pack.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* File Management Area */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
          {selectedLessonPack ? (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Files for {selectedLessonPack.lesson_pack_subject}
                </h2>
                <p className="text-gray-600">
                  Tutor: {selectedLessonPack.lesson_pack_tutor_name}
                </p>
              </div>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : uploading
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {uploading ? (
                  <div>
                    <CloudArrowUpIcon className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">Uploading...</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600">{Math.round(uploadProgress)}% complete</p>
                  </div>
                ) : (
                  <div>
                    <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      Drop files here or click to upload
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      Support for PDF, images, documents, and more
                    </p>
                    <input
                      type="file"
                      onChange={handleFileInput}
                      className="hidden"
                      id="file-upload"
                      accept="*/*"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>

              {/* Files List */}
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Files ({files.length})</h3>
                
                {files.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FolderIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No files uploaded yet</p>
                    <p className="text-sm">Upload your first file above</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-2xl">{getFileIcon(file.file_type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{file.file_name}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                              <span>{formatFileSize(file.file_size)}</span>
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {formatDate(file.uploaded_at)}
                              </span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                file.uploaded_by_type === 'tutor' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : file.uploaded_by_type === 'student'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {file.uploaded_by_type === 'tutor' ? '👨‍🏫' : file.uploaded_by_type === 'student' ? '👨‍🎓' : '❓'} 
                                {file.uploaded_by_display}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownload(file)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <BookOpenIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a lesson pack to view files</p>
              <p className="text-sm">Choose from the lesson packs on the left</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentFileSharing;